import {
    Card, Box, Typography, Grid, IconButton, Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { styled } from '@mui/material/styles';
import { RelevanceScore } from 'src/types/RelevanceScore';
import { config } from 'src/lib/config';
import { ScoreControls } from './ScoreControls';
import { CommentBox } from './CommentBox';

const ImageContainer = styled(Box)(() => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    overflow: 'hidden',
}));

const StyledImage = styled('img')(() => ({
    width: '100%',
    maxHeight: '300px',
    objectFit: 'contain',
}));

type PropTypes = {
    backgroundColor: string;
    entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity;
    handleCommentChange?: (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        comment: string
    ) => void;
    handleScoredEntityChange?: (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        score: Models.IngestConfigApi.RelevanceTests.RelevanceScore
    ) => void;
    showScoreControls?: boolean;
    showCommentBox?: boolean;
    markChanged?: () => void;
    productKey: string;
};

export const TestEntityCard = (props: PropTypes): JSX.Element => {
    const {
        backgroundColor,
        entity,
        handleCommentChange,
        handleScoredEntityChange,
        showScoreControls = false,
        showCommentBox = false,
        markChanged,
        productKey,
    } = props;

    const taggingToolUrl = `${config.taggingRootUrl}?productKey=${productKey}&designConceptId=${entity.designConceptId}`;

    return (
        <Card
            sx={{
                mb: 2,
                backgroundColor,
                borderRadius: '12px',
                boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08)',
            }}
        >
            <Grid container alignItems="flex-start">
                <Grid item p={2} xs={6}>
                    <ImageContainer>
                        <StyledImage
                            alt={entity.designConceptId}
                            src={entity.previewUrl}
                        />
                    </ImageContainer>
                </Grid>
                <Grid item p={2} xs={6}>
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', gap: 1, mb: 2,
                    }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography sx={{ flexGrow: 1 }} variant="body2">
                                <strong>Design Concept ID:</strong>
                                {' '}
                                {entity.designConceptId}
                            </Typography>
                            <Tooltip title="View design concept in tagging tool">
                                <IconButton
                                    href={taggingToolUrl}
                                    rel="noopener noreferrer"
                                    size="small"
                                    target="_blank"
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Typography variant="body2">
                            <strong>Keywords:</strong>
                            {' '}
                            {Array.isArray(entity.keywords) ? entity.keywords.join(', ') : entity.keywords}
                        </Typography>

                        <Typography variant="body2">
                            <strong>Categories:</strong>
                            {' '}
                            {Array.isArray(entity.categories) ? entity.categories.join(', ') : entity.categories}
                        </Typography>
                    </Box>
                    {showScoreControls && handleScoredEntityChange && (
                        <ScoreControls
                            entity={entity}
                            markChanged={markChanged}
                            onScoreChange={handleScoredEntityChange}
                        />
                    )}
                    {!showScoreControls && entity.relevance === RelevanceScore.NotScored && (
                        <Typography style={{ fontStyle: 'italic' }} variant="body2">Entity not scored at time of test run</Typography>
                    )}
                    {showCommentBox && handleCommentChange && (
                        <CommentBox
                            entity={entity}
                            onCommentChange={handleCommentChange}
                        />
                    )}
                </Grid>
            </Grid>
        </Card>
    );
};
